import { createRouter, createWebHistory } from "vue-router";
import {
  getExpiresIn,
  getToken,
  getJobseekerToken,
} from "@/core/services/jwt.service";
import store from "@/core/services/store";

function checkLogin(next, from) {
  const expire_in = getExpiresIn();
  const time = Math.floor(new Date().getTime() / 1000);
  if (expire_in > time && getToken() && from.name !== "Invite") {
    next("/dashboard");
  } else {
    return next();
  }
}

async function checkJobseekerLogin(next, formRoute) {
  if (!getJobseekerToken()) {    
    if (formRoute) return next({ name: "mehr-bank-login" })
    return next({ name: "jobseeker-login" })
  }
  return next();
}

var restrictedPaths = ["plans", "invoice", "invoice-detail", "ticket"];

const routes = [
  {
    path: "/",
    redirect:
      location.href == "https://soha.work/" || location.href == "https://www.soha.work/"
        ? "/job"
        : "/dashboard",
    component: () => import("./view/layout/Layout"),
    children: [
      {
        path: "/dashboard",
        component: () => import("./view/pages/dashboard/Dashboard"),
        children: [
          {
            path: "",
            name: "dashboard",
            component: () => import("./view/pages/dashboard/DashboardIndex.vue"),
          },
        ],
      },

      {
        path: "/publisher",
        name: "publisher",
        component: () => import("@/view/pages/publisher/PublisherIndex"),
      },

      {
        path: "/jobs",
        component: () => import("@/view/pages/job/Job"),
        redirect: "jobs/index",
        children: [
          {
            path: "index",
            name: "jobs-list",
            component: () => import("@/view/pages/job/JobsIndex"),
          },
          {
            path: "create",
            name: "jobs-create",
            component: () => import("@/view/pages/job/create/CreateJobIndex"),
          },
          {
            path: "edit",
            name: "jobs-edit",
            component: () => import("@/view/pages/job/edit/EditJobIndex"),
          },
          {
            path: "publishers",
            name: "job-publishers",
            component: () => import("@/view/pages/job/JobPublishers"),
          },
          {
            path: "pipeline",
            name: "job-pipeline-step",
            query: "jobId",
            component: () => import("@/view/pages/job/JobPipeline"),
          },
        ],
      },

      {
        path: "/resumes",
        redirect: "resumes/index",
        component: () => import("./view/pages/resumes/Resume"),
        children: [
          {
            path: "index",
            name: "resumes-list",
            component: () => import("./view/pages/resumes/ResumeIndex"),
          },
          {
            path: "detail",
            name: "resume-detail",
            component: () => import("./view/pages/resumes/ResumeDetail"),
          },
          {
            path: "mass-upload",
            name: "mass-upload",
            component: () => import("./view/pages/resumes/ResumeMassUpload"),
          },
          {
            path: "search",
            name: "resume-search",
            component: () => import("./view/pages/resumes/ResumeSearch"),
          },
          {
            path: "scoring",
            name: "resume-scoring",
            component: () => import("./view/pages/resumes/ResumeScoringPage"),
          },
        ],
      },

      {
        path: "/recruitment-requests",
        name: "recruitment-requests",
        redirect: { name: "recruitment-request-list" },
        component: () => import("./view/pages/recruitment-request/RecruitmentRequest"),
        beforeEnter: async (to, from, next) => {
          if (!store.getters.menuItems.length) {
            try {
              await store.dispatch("fetchMenu");
            } catch (error) {
              return next();
            }
          }

          const settingMenu = store.getters.menuItems.find((item) => item.id == 8);
          const firstSubPath = settingMenu?.subMenus?.[0]?.url;

          if (firstSubPath && to.path == "/recruitment-requests") {
            return next(firstSubPath);
          }

          return next();
        },
        children: [
          {
            path: "index",
            name: "recruitment-request-list",
            component: () => import("./view/pages/recruitment-request/RecruitmentRequestIndex"),
          },
          {
            path: "users",
            name: "recruitment-request-list-user",
            component: () => import("./view/pages/recruitment-request/RecruitmentRequestUserIndex"),
          },
          {
            path: "create",
            name: "recruitment-request-create",
            component: () => import("./view/pages/recruitment-request/RecruitmentRequestCreate"),
          },
          {
            path: "show",
            name: "recruitment-request-show",
            component: () => import("./view/pages/recruitment-request/RecruitmentRequestShow"),
          },
        ],
      },

      {
        path: "/setting",
        name: "setting",
        redirect: { name: "resume-form" },
        component: () => import("@/view/pages/setting/Setting"),
        beforeEnter: async (to, from, next) => {
          if (!store.getters.menuItems.length) {
            try {
              await store.dispatch("fetchMenu");
            } catch (error) {
              return next();
            }
          }

          const settingMenu = store.getters.menuItems.find((item) => item.id == 7);
          const firstSubPath = settingMenu?.subMenus?.[0]?.url;

          if (firstSubPath && to.path == "/setting") {
            return next(firstSubPath);
          }

          return next();
        },
        children: [
          {
            path: "form",
            name: "resume-form",
            component: () => import("./view/pages/setting/resume-form/ResumeForms"),
          },
          {
            path: "manage-form/:id?",
            name: "manage-resume-form",
            component: () => import("./view/pages/setting/resume-form/ResumeFormCreateAndEdit"),
          },
          {
            path: "edit-user",
            name: "edit-user",
            component: () => import("./view/pages/setting/EditUserInfo"),
          },
          {
            path: "edit-company",
            name: "edit-company",
            component: () => import("./view/pages/setting/EditCompany"),
          },
          {
            path: "steps",
            name: "custom-step",
            component: () => import("./view/pages/setting/steps/Steps"),
          },
          {
            path: "manage-step/:id?",
            name: "manage-step",
            component: () => import("./view/pages/setting/steps/ManageSteps"),
          },
          {
            path: "integration",
            name: "integration-job-board",
            component: () => import("./view/pages/setting/IntegrationJobBoard"),
          },
          {
            path: "notification",
            name: "notification-setting",
            component: () => import("./view/pages/setting/Notification"),
          },
          {
            path: "message-pattern",
            name: "message-pattern",
            component: () => import("./view/pages/setting/MessagePattern"),
          },
          {
            path: "reject-user",
            name: "reject-reason-setting",
            component: () => import("./view/pages/setting/RejectReason"),
          },
          {
            path: "test",
            name: "custom-test-setting",
            component: () => import("./view/pages/setting/custom-test/CustomTest"),
          },
          {
            path: "create-edit-test/:id?",
            name: "create-edit-custom-test",
            component: () => import("./view/pages/setting/custom-test/CustomTestCreateEdit"),
          },
          {
            path: "checklist",
            name: "checklist-setting",
            component: () => import("./view/pages/setting/checklist/CheckList"),
          },
          {
            path: "create-edit-checklist/:id?",
            name: "create-edit-checklist",
            component: () => import("./view/pages/setting/checklist/CheckListCreateEdit"),
          },
          {
            path: "linked-id",
            name: "linked-id",
            component: () => import("@/view/pages/setting/LinkedId"),
          },
          {
            path: "competencies",
            name: "competencies",
            component: () => import("@/view/pages/setting/Competencies"),
          },
          {
            path: "scoring-card",
            name: "scoring-card",
            component: () => import("./view/pages/setting/scoring-card/Index"),
          },
          {
            path: "create-edit-scoring-card/:id?",
            name: "create-edit-scoring-card",
            component: () => import("./view/pages/setting/scoring-card/CreateEdit"),
          },
          {
            path: "request-flow",
            name: "recruitment-request-flow",
            component: () => import("./view/pages/setting/recruitment-request/Flow"),
          },
          {
            path: "manage-flow/:id?",
            name: "manage-flow",
            component: () => import("./view/pages/setting/recruitment-request/ManageFlow"),
          },
          {
            path: "tags",
            name: "tag-list",
            component: () => import("./view/pages/setting/TagIndex"),
          },
          {
            path: "role",
            component: () => import("@/view/pages/setting/roles/Role"),
            children: [
              {
                path: "",
                name: "roles-list",
                component: () => import("@/view/pages/setting/roles/RolesIndex"),
              },
              {
                path: "create",
                name: "roles-create",
                component: () => import("@/view/pages/setting/roles/CreateEditShowRole"),
              },
              {
                path: "edit/:id?",
                name: "roles-edit",
                component: () => import("@/view/pages/setting/roles/CreateEditShowRole"),
              },
              {
                path: "show",
                name: "roles-show",
                component: () => import("@/view/pages/setting/roles/CreateEditShowRole"),
              },
            ],
          },
          {
            path: "members",
            name: "members-list",
            component: () => import("./view/pages/setting/Members"),
          },
          {
            path: "invitation",
            redirect: "invitation/index",
            component: () => import("@/view/pages/setting/invitation/Invitation"),
            children: [
              {
                path: "index",
                name: "invitation-list",
                component: () => import("./view/pages/setting/invitation/InvitationIndex"),
              },
              {
                path: "invite",
                name: "invitation-invite",
                component: () => import("@/view/pages/setting/invitation/InvitationNewMember"),
              },
            ],
          },
          {
            path: "rules",
            name: "rule-template",
            component: () => import("./view/pages/setting/RuleTemplate"),
          },
        ],
      },

      {
        path: "/reports",
        name: "reports",
        redirect: { name: "visit-reports" },
        component: () => import("@/view/pages/reports/Report"),
        beforeEnter: async (to, from, next) => {
          if (!store.getters.menuItems.length) {
            try {
              await store.dispatch("fetchMenu");
            } catch (error) {
              return next();
            }
          }

          const settingMenu = store.getters.menuItems.find((item) => item.id == 6);
          const firstSubPath = settingMenu?.subMenus?.[0]?.url;

          if (firstSubPath && to.path == "/reports") {
            return next(firstSubPath);
          }

          return next();
        },
        children: [
          {
            path: "visit",
            name: "visit-reports",
            component: () => import("@/view/pages/reports/ReportVisit"),
          },
          {
            path: "jobSeeker",
            name: "job-seeker-reports",
            component: () => import("@/view/pages/reports/ReportJobSeekers"),
          },
          {
            path: "employee",
            name: "employee-reports",
            component: () => import("@/view/pages/reports/ReportEmployees"),
          },
          {
            path: "message",
            name: "message-reports",
            component: () => import("@/view/pages/reports/ReportMessage"),
          },
        ],
      },

      {
        path: "/personality-test-answer/:testId/:resumeId",
        name: "personality-test-answer-resume",
        component: () =>
          import("@/view/pages/personality-tests/PersonalityTestAnswer"),
      },

      {
        path: "/custom-test",
        name: "custom-test",
        component: () => import("@/view/pages/custom-tests/CustomTest"),
        children: [
          {
            path: "answer/:testId/:resumeId/:linkId",
            name: "resume-custom-test-answer",
            component: () =>
              import("@/view/pages/custom-tests/CustomTestAnswer"),
          },
          {
            path: "answers/:testId/:resumeId",
            name: "resume-custom-test-answers",
            component: () =>
              import("@/view/pages/custom-tests/CustomTestAnswers"),
          },
        ],
      },

      {
        path: "/task",
        name: "task",
        redirect: { name: "resume-assign" },
        component: () => import("@/view/pages/task/Task"),
        children: [
          {
            path: "resume-assign",
            name: "resume-assign",
            component: () => import("./view/pages/task/TaskResumeAssign"),
          },
          {
            path: "interview",
            name: "interview-calendar",
            component: () => import("@/view/pages/task/TaskInterview"),
          },
          {
            path: "employee",
            name: "task-employee",
            component: () => import("@/view/pages/task/TaskEmployee"),
          },
        ],
      },

      {
        path: "/ticket",
        name: "ticket",
        redirect: { name: "ticket-index" },
        component: () => import("@/view/pages/ticket/Ticket"),
        children: [
          {
            path: "",
            name: "ticket-index",
            component: () => import("@/view/pages/ticket/TicketIndex"),
          },
          {
            path: "detail",
            name: "ticket-detail",
            query: "ticketId",
            component: () => import("@/view/pages/ticket/TicketDetail"),
          },
        ],
      },

      {
        path: "/financial",
        name: "financial",
        redirect: { name: "plans" },
        component: () => import("@/view/pages/financial/Financial"),
        beforeEnter: async (to, from, next) => {
          if (!store.getters.menuItems.length) {
            try {
              await store.dispatch("fetchMenu");
            } catch (error) {
              return next();
            }
          }

          const settingMenu = store.getters.menuItems.find(
            (item) => item.id == 9
          );
          const firstSubPath = settingMenu?.subMenus?.[0]?.url;

          if (firstSubPath && to.path == "/financial") {
            return next(firstSubPath);
          }

          return next();
        },
        children: [
          {
            path: "plans",
            name: "plans",
            component: () => import("@/view/pages/financial/Plan"),
          },
          {
            path: "invoice",
            name: "invoice",
            component: () => import("@/view/pages/financial/Invoice"),
          },
          {
            path: "invoice-detail",
            name: "invoice-detail",
            query: "invoiceId",
            component: () => import("@/view/pages/financial/InvoiceDetail"),
          },
          {
            path: "wallet-charge",
            name: "wallet-charge",
            component: () => import("@/view/pages/financial/WalletCharge"),
          },
        ],
      },

      // ... (keep the rest routes as-is) ...
    ],
    beforeEnter: async (to, from, next) => {
      // await store.dispatch("employerInfo");
      if (getToken() && !to.meta?.noAuth) {
        if (store.getters.remainingDay == 0 && !restrictedPaths.includes(to.name))
          next("/financial/plans?endPlan=true");
        else return next();
      } else if (!to.meta?.noAuth) next("/login");
      return next();
    },
  },
  {
    path: "/auth",
    component: () => import("./view/pages/auth/Auth"),
    children: [
      {
        name: "login",
        path: "/login",
        meta: { noAuth: true },
        component: () => import("./view/pages/auth/Login"),
        beforeEnter: (to, from, next) => {
          checkLogin(next, from);
        },
      },
      {
        name: "auto-login",
        path: "/autoLogin",
        query: "token",
        meta: { noAuth: true },
        component: () => import("./view/pages/auth/AutoLogin"),
      },
      {
        name: "register",
        path: "/register",
        meta: { noAuth: true },
        component: () => import("./view/pages/auth/register/Index"),
        beforeEnter: (to, from, next) => {
          checkLogin(next);
        },
      },
      {
        name: "forget-password",
        path: "/forget-password",
        meta: { noAuth: true },
        component: () => import("./view/pages/auth/ForgetPassword"),
        beforeEnter: (to, from, next) => {
          checkLogin(next);
        },
      },
      {
        name: "reset-password",
        path: "/reset-password",
        meta: { noAuth: true },
        component: () => import("./view/pages/auth/ResetPassword"),
        beforeEnter: (to, from, next) => {
          checkLogin(next);
        },
      },
      {
        name: "Invite",
        path: "/inv/:inviteId",
        meta: { noAuth: true },
        component: () => import("@/view/pages/auth/Invite"),
      },
      {
        name: "pre-registration",
        path: "pre-registration",
        meta: { noAuth: true },
        component: () => import("@/view/pages/auth/register/RegisterPreRegistration"),
      },
    ],
  },

  {
    path: "/pt/:linkId/:resumeId/:testId",
    redirect: to => `/personality-test/${to.params.testId}/${to.params.linkId}/${to.params.resumeId}`,
    meta: { noAuth: true },
  },
  {
    path: "/pt/:testId",
    redirect: to => `/personality-test/${to.params.testId}`,
    meta: { noAuth: true },
  },
  {
    path: "/ct/:resumeId/:testId/:linkId",
    redirect: to => `/custom-test/${to.params.testId}/${to.params.linkId}/${to.params.resumeId}`,
    meta: { noAuth: true },
  },
  {
    path: "/personality-test/:testId",
    name: "personality-test-public",
    meta: { noAuth: true },
    component: () =>
      import("@/view/pages/personality-tests/PersonalityTestQuestions"),
  },
  {
    path: "/personality-test/:testId/:linkId/:resumeId",
    name: "personality-test-resume",
    meta: { noAuth: true },
    component: () =>
      import("@/view/pages/personality-tests/PersonalityTestQuestions"),
  },
  {
    path: "/personality-test-answer-public/:testId/:uuid",
    name: "personality-test-answer-public",
    meta: { noAuth: true },
    component: () =>
      import("@/view/pages/personality-tests/PersonalityTestAnswer"),
  },
  {
    path: "/custom-test/:testId/:linkId/:resumeId",
    name: "custom-test-resume",
    meta: { noAuth: true },
    component: () => import("@/view/pages/custom-tests/CustomTestQuestions"),
  },
  {
    name: "guest",
    path: "/guest/:guId",
    meta: { noAuth: true },
    component: () => import("@/view/pages/resumes/ResumeDetailForGuest"),
  },
  {
    path: "/jobOpportunity",
    name: "job-opportunity",
    meta: { noAuth: true },
    component: () => import("./view/pages/job/JobOpportunity"),
  },
  {
    path: "/:pathMatch(.*)*",
    meta: { noAuth: true },
    redirect: "/404",
  },
  {
    // the 404 route, when none of the above matches
    path: "/404",
    name: "404",
    meta: { noAuth: true },
    component: () => import("./view/pages/error/Error.vue"),
  },
  {
    // the 404 route, when none of the above matches
    path: "/maintenance",
    name: "maintenance",
    meta: { noAuth: true },
    component: () => import("./view/pages/error/Error503.vue"),
  },
  {
    path: "/jobseeker",
    meta: { jobseeker: true },
    component: () => import("@/jobseeker/layout/Layout"),
    children: [
      {
        name: "jobseeker-dashboard",
        path: "dashboard",
        meta: { jobseekerAuth: true, jobseeker: true },
        component: () => import("@/jobseeker/pages/dashboard/Dashboard"),
      },
      {
        name: "jobseeker-resume",
        path: "resume",
        meta: { jobseekerAuth: true, jobseeker: true },
        component: () => import("@/jobseeker/pages/resume/Resume"),
      },
      {
        name: "jobseeker-job-preferences",
        path: "preferences",
        meta: { jobseekerAuth: true, jobseeker: true },
        component: () => import("@/jobseeker/pages/job/JobPreferences"),
      },
      {
        name: "jobseeker-history",
        path: "history",
        meta: { jobseekerAuth: true, jobseeker: true },
        component: () => import("@/jobseeker/pages/history/History"),
      },
    ],
    beforeEnter: (to, from, next) => {
      checkJobseekerLogin(next);
    },
  },
  {
    path: "/jobseeker-auth",
    meta: { jobseeker: true },
    component: () => import("@/jobseeker/pages/auth/Auth"),
    children: [
      {
        name: "jobseeker-login",
        path: "login",
        meta: { noAuth: true, jobseeker: true },
        component: () => import("@/jobseeker/pages/auth/Login"),
      },
      {
        name: "jobseeker-register",
        path: "register",
        meta: { noAuth: true, jobseeker: true },
        component: () => import("@/jobseeker/pages/auth/Register"),
      },
      {
        name: "jobseeker-forget-password",
        path: "forget-password",
        meta: { noAuth: true, jobseeker: true },
        component: () => import("@/jobseeker/pages/auth/ForgetPassword"),
      },
      {
        name: "jobseeker-reset-password",
        path: "reset-password/:username/:code",
        meta: { noAuth: true, jobseeker: true },
        component: () => import("@/jobseeker/pages/auth/ResetPassword"),
      },
    ],
  },
  {
    path: "/job/:seoPath?",
    name: "jobseeker-jobs",
    meta: { noAuth: true },
    component: () => import("@/jobseeker/pages/job/Job"),
    children: [
      {
        path: "",
        name: "jobseeker-jobs-list",
        query: "jobId",
        meta: { noAuth: true },
        component: () => import("@/jobseeker/pages/job/JobIndex"),
      },
      {
        path: "/job-ad/:jobId/:publisherId/:jobTitle?",
        name: "jobseeker-job-detail",
        meta: { noAuth: true },
        component: () => import("@/jobseeker/pages/job/JobDetail"),
      },
    ],
  },

  {
    path: "/m-login",
    name: "mehr-bank-login",
    meta: { noAuth: true },
    component: () =>
      import("@/jobseeker/pages/mehr-bank/Login.vue"),
  },

  {
    path: "/m-form",
    name: "mehr-bank-form",
    meta: { noAuth: true },
    component: () =>
      import("@/jobseeker/pages/mehr-bank/Form.vue"),
    beforeEnter: (to, from, next) => {
      checkJobseekerLogin(next, true);
    },
  },

  {
    path: "/shahrak-isfahan/:seoPath?",
    name: "shahrak-isfahan",
    meta: { noAuth: true },
    component: () =>
      import("@/jobseeker/pages/exclusive-publishers/ShahrakIsfahan"),
  },
  {
    path: "/eccim/:seoPath?",
    name: "eccim-isfahan",
    meta: { noAuth: true },
    component: () =>
      import("@/jobseeker/pages/exclusive-publishers/EccimIsfahan"),
  },
  {
    path: "/nano/:seoPath?",
    name: "nano",
    meta: { noAuth: true },
    component: () => import("@/jobseeker/pages/exclusive-publishers/Nano"),
  },

  // ... (rest top-level routes - keep identical to original)
];

/* create router */
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL || "/"),
  routes,
});


if (process.env.VUE_APP_FOOLAD_TECHNIC_SERVER != "true") {
  router.beforeEach(async (to, from, next) => {
    const isJobseekerRoute = to.meta?.jobseeker;
    const noAuth = to.meta?.noAuth;

    // اگر روت noAuth باشه، مستقیم برو
    if (noAuth) return next();

    // پنل jobseeker
    if (isJobseekerRoute) {
      if (getJobseekerToken()) {
        return next();
      } else {
        return next("/jobseeker-auth/login");
      }
    }

    // پنل employer (پیش‌فرض)
    if (getToken()) {
      if (!Object.keys(store.getters.userInfo).length) {
        await store.dispatch("employerInfo");
      }
      if (store.getters.remainingDay == 0 && !restrictedPaths.includes(to.name)) {
        return next("/financial/plans?endPlan=true");
      } else {
        return next();
      }
    } else {
      return next("/login");
    }
  });
} else {
  router.beforeEach((to, from, next) => {
    if (getToken() || to.meta?.noAuth) {
      return next();
    } else {
      return next("/login");
    }
  });
}

export default router;
